export type StrategyId = 'service' | 'lean' | 'balanced' | 'utilization' | 'band_utilization';

export interface StrategySelection {
  strategy: StrategyId;
  priorities: string[];
  targetUtilizationPct?: number;
}

export const SECONDARY_PRIORITIES = {
  'balance-workload': { name: 'Balance driver workload', description: 'Favor drivers with room for more work.' },
  'driver-pay': { name: 'Support driver pay goals', description: 'Help drivers reach their daily earning goals.' },
  'nearby-work': { name: 'Keep work close', description: 'Favor drivers closer to the next pickup.' },
  'nearby-returns': { name: 'Keep returns nearby', description: 'Limit return matches to 10 miles, or the region’s tighter limit.' },
  'leave-slack': { name: 'Leave room in the day', description: 'Allow at least 60 minutes between moves where the planner applies a buffer.' },
} as const;

export type PriorityId = keyof typeof SECONDARY_PRIORITIES;

export const STRATEGIES: {
  id: StrategyId;
  name: string;
  eyebrow: string;
  description: string;
  emphasis: string;
  priorities: PriorityId[];
}[] = [
  {
    id: 'service', name: 'Max out & pick up ahead', eyebrow: 'Plan A',
    description: 'Every driver to their own busy-day hours · idle trucks grab the nearest load · tomorrow’s releasable freight picked up today',
    emphasis: 'Fill the day · pick up ahead', priorities: ['balance-workload', 'driver-pay', 'nearby-work'],
  },
  {
    id: 'lean', name: 'Fewest trucks · least bobtail', eyebrow: 'Plan B',
    description: 'Longest legal shifts, densest chains · reuse compatible chassis and minimize empty trips',
    emphasis: 'Fewer trucks · less bobtail', priorities: ['nearby-work', 'nearby-returns', 'driver-pay'],
  },
  {
    id: 'balanced', name: 'Guaranteed on-time', eyebrow: 'Plan C',
    description: 'Every driver capped at their average day · fully executable, every appointment in-window',
    emphasis: 'Average-day hours · verified windows', priorities: ['leave-slack', 'balance-workload', 'nearby-work'],
  },
  {
    id: 'utilization', name: 'Driving utilization', eyebrow: 'Plan D',
    description: 'Choose a driving target within the 11-hour driving limit · prioritize underfilled drivers · service and waiting do not count',
    emphasis: 'Adjustable driving target · every available driver', priorities: [],
  },
  {
    id: 'band_utilization', name: 'Mileage band utilization', eyebrow: 'Plan E',
    description: 'Plan D with each driver locked to their first delivery’s mileage band · only the delivery leg and the leg after it · one-way miles · resets each plan',
    emphasis: 'Compare band efficiency · delivery and return legs', priorities: [],
  },
];

export function changeStrategy(selection: StrategySelection, strategy: StrategyId): StrategySelection {
  const allowed = STRATEGIES.find(item => item.id === strategy)!.priorities;
  return { ...selection, strategy, priorities: selection.priorities.filter(id => allowed.includes(id as PriorityId)) };
}

export function togglePriority<T extends StrategySelection>(selection: T, priority: string): T {
  const allowed = STRATEGIES.find(item => item.id === selection.strategy)!.priorities;
  if (!allowed.includes(priority as PriorityId)) return selection;
  if (selection.priorities.includes(priority)) {
    return { ...selection, priorities: selection.priorities.filter(id => id !== priority) };
  }
  if (selection.priorities.length >= 2) return selection;
  return { ...selection, priorities: [...selection.priorities, priority] };
}

export function strategyRequest(selection: StrategySelection) {
  return { primary: selection.strategy, secondary: ['utilization', 'band_utilization'].includes(selection.strategy) ? [] : selection.priorities as PriorityId[],
    ...(['utilization', 'band_utilization'].includes(selection.strategy) ? { target_utilization_pct: selection.targetUtilizationPct ?? 85 } : {}) };
}
